import React, { useEffect, useRef, useState } from "react";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import HomeIcon from "@mui/icons-material/Home";
import CircleStyle from "ol/style/Circle";
import useMap from "./useMap";
import DfrMap from "./dfrMap";
import { Feature } from "ol";
import { Cluster } from "ol/source";
import { Point } from "ol/geom.js";
import { Box, MenuItem, MenuList, Paper, Popper, Typography } from "@mui/material";
import { Fill, Stroke, Style, Text } from "ol/style";
import { ConvertDfrToFeatures, ConvertISOToDate, JumpToFeatures } from "./dfrUtil";

export default function DfrHeatMap({ dfrs }) {
    const MAP_ID = `map-dfr-heat`;
    const map = useMap(MAP_ID, false);

    const [dfrToTelemetry, setDfrToTelemetry] = useState(new Map());
    const [telemetryReady, setTelemetryReady] = useState(false);
    const sourcePoints = useRef(new VectorSource());
    const sourceVolumes = useRef(new VectorSource());

    useEffect(() => {
        const fetchData = async () => {
            const dfrToTelemetry = new Map();
            for (const dfr of dfrs) {
                if (!dfr.telemetry) {
                    continue;
                }

                const requestOptions = {
                    method: "GET",
                    headers: { "Content-Type": "application/json" }
                };
                const telemetry = await fetch(`/api/telemetry/get/${dfr.flight_uuid}`, requestOptions)
                    .then((response) => (response.ok ? response.json() : Promise.reject(response)))
                    .catch((err) => console.error("DFR Query Error", err.stack));

                if (telemetry) {
                    dfrToTelemetry.set(dfr, telemetry);
                }
            }

            setDfrToTelemetry(dfrToTelemetry);
            setTelemetryReady(true);
        };

        setTelemetryReady(false);
        fetchData();
    }, [dfrs]);

    useEffect(() => {
        if (!map) {
            return;
        }

        const handlePointerMove = (event) => {
            let selectable = false;
            map.forEachFeatureAtPixel(event.pixel, (_, layer) => {
                if (!selectable) {
                    selectable = layer.get("name") === "dfr" || layer.get("name") === "clusters";
                }
            });
            map.getTargetElement().style.cursor = selectable ? "pointer" : "";
        };

        const handleClick = (event) => {
            clusters.getFeatures(event.pixel).then((features) => {
                if (features.length === 0) {
                    return;
                }

                const clusterMembers = features[0].get("features");
                if (clusterMembers.length >= 1) {
                    JumpToFeatures(map, clusterMembers);
                }
            });
        };

        // Layer displaying the full volumes of the DFR structures.
        const dfrLayer = new VectorLayer({
            source: sourceVolumes.current,
            zIndex: 1,
            properties: { name: "dfr" }
        });

        // Layer displaying the clusters and individual features.
        const clusters = new VectorLayer({
            source: new Cluster({ source: sourcePoints.current, distance: 100 }),
            style: clusterStyle,
            zIndex: 2,
            properties: { name: "clusters" }
        });

        map.addLayer(dfrLayer);
        map.addLayer(clusters);
        map.on("pointermove", handlePointerMove);
        map.on("click", handleClick);

        return () => {
            map.un("pointermove", handlePointerMove);
            map.un("click", handleClick);
        };
    }, [map]);

    useEffect(() => {
        if (!map || !telemetryReady) {
            return;
        }

        sourcePoints.current.clear();
        sourceVolumes.current.clear();

        // Setup DFR volumes.
        for (const dfr of dfrs) {
            const features = ConvertDfrToFeatures(dfr, dfrToTelemetry.get(dfr));
            for (const feature of features) {
                const geometry = feature.getGeometry();
                const center = geometry.getType() === "Polygon" ? geometry.getInteriorPoint() : new Point(geometry.getCoordinateAt(0));
                sourcePoints.current.addFeature(new Feature(center));
                sourceVolumes.current.addFeature(feature);
            }
        }

        JumpToFeatures(map, sourceVolumes.current.getFeatures());
    }, [map, dfrToTelemetry, telemetryReady]);

    const clusterStyle = (feature, resolution) => {
        const size = feature.get("features").length;
        const zoom = map.getView().getZoomForResolution(resolution);
        if (size === 1 && zoom >= 12) {
            return null;
        }

        return [
            new Style({
                image: new CircleStyle({
                    radius: 40,
                    fill: new Fill({ color: "rgba(255, 153, 102, 0.3)" })
                })
            }),
            new Style({
                image: new CircleStyle({
                    radius: 30,
                    fill: new Fill({ color: "rgba(255, 128, 0, 0.7)" })
                }),
                text: new Text({
                    text: size.toString(),
                    fill: new Fill({ color: "#fff" }),
                    stroke: new Stroke({ color: "rgba(0, 0, 0, 0.6)", width: 5 })
                })
            })
        ];
    };

    const handleHome = () => {
        JumpToFeatures(map, sourceVolumes.current.getFeatures());
    };

    return (
        <DfrMap id={MAP_ID} buttons={[{ name: "View Home", icon: <HomeIcon />, onClick: handleHome }]}>
            <DfrInfoCard map={map} />
        </DfrMap>
    );
}

function DfrInfoCard({ map }) {
    const [fullscreenElement, setFullscreenElement] = useState(null);
    const [popperPosition, setPopperPosition] = useState(null);
    const [featuresClicked, setFeaturesClicked] = useState([]);
    const [featureInfo, setFeatureInfo] = useState([]);

    useEffect(() => {
        const handleFullscreen = () => setFullscreenElement(document.fullscreenElement);
        document.addEventListener("fullscreenchange", handleFullscreen);
        return () => document.removeEventListener("fullscreenchange", handleFullscreen);
    }, []);

    useEffect(() => {
        if (!map) {
            return;
        }

        const handleMapSelection = ({ pixel, originalEvent }) => {
            if (Array.isArray(featuresClicked) && featuresClicked.length > 1) {
                setFeaturesClicked([]);
                return;
            }

            const features = [];
            map.forEachFeatureAtPixel(pixel, (feature, layer) => {
                if (layer.get("name") === "dfr") {
                    features.push(feature);
                }
            });

            if (features.length === 0) {
                setFeaturesClicked(features);
                setFeatureInfo([]);
            } else if (features.length === 1) {
                setFeaturesClicked([features[0]]);
            } else {
                const virtualElement = {
                    getBoundingClientRect: () => ({
                        width: 0,
                        height: 0,
                        top: originalEvent.y,
                        right: originalEvent.x,
                        bottom: originalEvent.y,
                        left: originalEvent.x
                    }),
                    nodeType: 1
                };
                setPopperPosition(virtualElement);
                setFeaturesClicked(features);
            }
        };

        map.on("click", handleMapSelection);

        return () => {
            map.un("click", handleMapSelection);
        };
    }, [map, featuresClicked]);

    useEffect(() => {
        // We will only get the info of a feature if there is only 1 selected.
        if (featuresClicked.length !== 1) {
            return;
        }

        // Get the entity's info to display in the info card.
        const dfr = featuresClicked[0].get("dfr");
        const info = [
            { title: "Start Time", value: ConvertISOToDate(dfr.time_start) },
            { title: "Organization", value: dfr.organization_name },
            { title: "Case/Incident", value: dfr.incident },
            { title: "Location", value: dfr.location },
            { title: "Summary", value: dfr.summary }
        ];

        setFeatureInfo(info);
    }, [featuresClicked]);

    return featuresClicked.length > 1 ? (
        // Shows the feature menu list if multiple features are selected at once.
        <Popper open={featuresClicked.length > 1} anchorEl={popperPosition} placement={"bottom-end"} container={fullscreenElement} sx={{ zIndex: 10000 }}>
            <Paper>
                <MenuList dense sx={{ maxHeight: 200, overflow: "auto" }}>
                    {featuresClicked.map((feature, i) => (
                        <MenuItem key={i} onClick={() => setFeaturesClicked([feature])}>
                            {feature.get("dfr").incident}
                        </MenuItem>
                    ))}
                </MenuList>
            </Paper>
        </Popper>
    ) : (
        featuresClicked.length === 1 && Array.isArray(featureInfo) && featureInfo.length > 0 && (
            <Paper
                elevation={6}
                sx={{
                    position: "absolute",
                    zIndex: "1000",
                    top: "45px",
                    right: "10px",
                    maxHeight: "calc(100% - 55px)",
                    width: { xs: "calc(100% - 20px)", sm: "300px" },
                    boxShadow: "0 4px 10px rgba(0, 0, 0, 0.2)",
                    p: "12px",
                    overflowY: "auto"
                }}
            >
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {featureInfo.map(({ title, value, color }, i) => (
                        <Box key={i} sx={{ display: "flex", flexDirection: "row", justifyContent: "space-between", gap: 1 }}>
                            <Typography variant="body2" fontWeight="bold" fontStyle="normal" sx={{ whiteSpace: "nowrap", color: color || "text.secondary" }}>
                                {title}
                            </Typography>
                            {value && (
                                <Typography variant="body2" sx={{ color: color || "text.secondary" }} fontStyle="normal">
                                    {value || "--"}
                                </Typography>
                            )}
                        </Box>
                    ))}
                </Box>
            </Paper>
        )
    );
}
